import { useState } from "react";
import { Lock, Plus, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Confirm, Pill } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function AddRoute({ eventId, onDone, nextCode }) {
  const [v, setV] = useState({ name: "", code: "", grade: "", route_type: "TOP_ROPE", rotation_minutes: 5 });
  const add = async () => {
    try {
      const { data } = await api.post(`/admin/events/${eventId}/routes`, { ...v, code: v.code || nextCode, name: v.name || `Route ${v.code || nextCode}`, rotation_minutes: parseInt(v.rotation_minutes, 10), display_order: 99 });
      toast.success("Jalur ditambahkan.");
      setV({ name: "", code: "", grade: "", route_type: "TOP_ROPE", rotation_minutes: 5 });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl bg-[#F5F3EB] p-3" data-testid="add-route-form">
      <Input className="w-36 bg-white" placeholder={`Route ${nextCode}`} value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="new-route-name-input" />
      <Input className="w-20 bg-white" placeholder={nextCode} value={v.code} onChange={(e) => setV({ ...v, code: e.target.value })} data-testid="new-route-code-input" />
      <Input className="w-24 bg-white" placeholder="Grade" maxLength={12} value={v.grade} onChange={(e) => setV({ ...v, grade: e.target.value })} data-testid="new-route-grade-input" />
      <Select value={v.route_type} onValueChange={(x) => setV({ ...v, route_type: x })}>
        <SelectTrigger className="w-36 bg-white" data-testid="new-route-type-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="TOP_ROPE">TOP_ROPE</SelectItem><SelectItem value="LEAD">LEAD</SelectItem></SelectContent>
      </Select>
      <Input className="w-24 bg-white" type="number" value={v.rotation_minutes} onChange={(e) => setV({ ...v, rotation_minutes: e.target.value })} data-testid="new-route-rotation-input" />
      <Button onClick={add} data-testid="add-route-button"><Plus className="mr-1 h-4 w-4" /> Tambah</Button>
    </div>
  );
}

export function EventRoutes({ event, setEvent }) {
  const reload = async () => setEvent((await api.get(`/admin/events/${event.id}`)).data);
  const toggle = async (r) => {
    try {
      const { data } = await api.post(`/keeper/routes/${r.id}/${r.status === "OPEN" ? "close" : "open"}`, {});
      toast.success(r.status === "OPEN" ? "Jalur ditutup untuk tiket baru. Antrean lama tetap berjalan." : "Jalur dibuka.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const update = async (r, patch) => {
    try {
      setEvent((await api.put(`/admin/routes/${r.id}`, patch)).data);
      toast.success("Jalur diperbarui.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (r) => {
    try {
      setEvent((await api.delete(`/admin/routes/${r.id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const used = new Set(event.routes.map((r) => r.code));
  const nextCode = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").find((c) => !used.has(c)) || "Z9";
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <Table data-testid="routes-table">
          <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Grade</TableHead><TableHead>Tipe</TableHead><TableHead>Rotasi (mnt)</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {event.routes.map((r) => (
              <TableRow key={r.id} data-testid={`route-row-${r.code}`}>
                <TableCell className="font-mono font-extrabold">{r.code}</TableCell>
                <TableCell><Input defaultValue={r.name} className="h-9 w-40" onBlur={(e) => e.target.value !== r.name && update(r, { name: e.target.value })} data-testid={`route-name-${r.code}`} /></TableCell>
                <TableCell><Input defaultValue={r.grade || ""} placeholder="5.10a / 6a" maxLength={12} className="h-9 w-24 font-mono" onBlur={(e) => e.target.value !== (r.grade || "") && update(r, { grade: e.target.value })} data-testid={`route-grade-${r.code}`} /></TableCell>
                <TableCell>
                  <Select value={r.route_type} onValueChange={(x) => update(r, { route_type: x, lead_required: x === "LEAD" })}>
                    <SelectTrigger className="h-9 w-32" data-testid={`route-type-${r.code}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="TOP_ROPE">TOP_ROPE</SelectItem><SelectItem value="LEAD">LEAD</SelectItem></SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Input type="number" defaultValue={r.rotation_minutes} className="h-9 w-20" onBlur={(e) => Number(e.target.value) !== r.rotation_minutes && update(r, { rotation_minutes: Number(e.target.value) })} data-testid={`route-rotation-${r.code}`} /></TableCell>
                <TableCell><Pill className={r.status === "OPEN" ? "bg-green-100 text-green-900" : "bg-stone-200 text-stone-700"}>{r.status === "OPEN" ? "Buka" : "Tutup"}</Pill></TableCell>
                <TableCell className="space-x-1 text-right">
                  {r.status === "OPEN" ? (
                    <Confirm testid={`admin-close-route-${r.code}`} title={`Tutup ${r.name}?`} desc="Penutupan hanya menghentikan tiket baru. Semua tiket yang sudah masuk tetap berjalan sampai selesai." destructive confirmText="Tutup" onConfirm={() => toggle(r)}
                      trigger={<Button size="sm" variant="outline" data-testid={`admin-close-route-${r.code}-button`}><Lock className="h-4 w-4" /></Button>} />
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`admin-open-route-${r.code}-button`}><Unlock className="h-4 w-4" /></Button>
                  )}
                  <Confirm testid={`delete-route-${r.code}`} title={`Hapus ${r.name}?`} desc="Hanya bisa jika belum ada tiket." destructive confirmText="Hapus" onConfirm={() => del(r)}
                    trigger={<Button size="sm" variant="ghost" data-testid={`delete-route-${r.code}-button`}><Trash2 className="h-4 w-4 text-red-600" /></Button>} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AddRoute eventId={event.id} nextCode={nextCode} onDone={setEvent} />
      <p className="text-xs text-muted-foreground">Durasi rotasi standar 5 menit adalah target operasional, bukan batas otomatis. Keeper tetap menekan SELESAI.</p>
    </div>
  );
}
